import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ElementRef,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ProfileService } from '../../services/profile.service';
import { CampaignService, Campaign, CampaignFilters } from '../../services/campaign.service';
import { CampaignSortUtils } from '../shared/campaign-sort.utils';
import { MobileDetectionMixin } from '../../shared/mixins/mobile-detection.mixin';
import { formatDateMonthDayYear } from '../../shared/utils/date.util';
import { DuplicateCampaignModalComponent } from '../duplicate-campaign-modal/duplicate-campaign-modal.component';
import { ArchiveCampaignModalComponent } from '../archive-campaign-modal/archive-campaign-modal.component';
import { PauseCampaignModalComponent } from '../pause-campaign-modal/pause-campaign-modal.component';
import { StopCampaignModalComponent } from '../stop-campaign-modal/stop-campaign-modal.component';
import { ViewCampaignModalComponent } from '../view-campaign-modal/view-campaign-modal.component';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import { CampaignPerformanceCellComponent } from '../../shared/components/campaign-performance-cell/campaign-performance-cell.component';
import { EmptyStateComponent } from '../../shared/components/empty-state';

// Remove the old Campaign interface as we're using the one from CampaignService

@Component({
  selector: 'app-campaign-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    MatSelectModule,
    DuplicateCampaignModalComponent,
    ArchiveCampaignModalComponent,
    PauseCampaignModalComponent,
    StopCampaignModalComponent,
    ViewCampaignModalComponent,
    EmptyStateComponent,
    CampaignPerformanceCellComponent,
  ],
  templateUrl: './campaign-table.component.html',
  styleUrls: ['./campaign-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CampaignTableComponent
  extends MobileDetectionMixin
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChild(MatSort) sort!: MatSort;

  private _tableScrollWrapper?: ElementRef<HTMLElement>;
  @ViewChild('tableScrollWrapper') set tableScrollWrapper(element: ElementRef<HTMLElement>) {
    if (element) {
      this._tableScrollWrapper = element;
      this.initializeDragScrolling();
    }
  }

  private dragScrollConfig!: DragScrollConfig;

  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() duplicate = new EventEmitter<any>();
  @Output() addCampaign = new EventEmitter<void>();

  @Input() campaigns: Campaign[] = [];
  @Input() loading = false;
  @Input() filters: CampaignFilters = {};
  @Input() originalCampaignCount = 0;
  @Input() hasLoadedInitialCampaigns = false;
  @Input() totalRecords = 0;

  // Number of rows shown in table (and skeleton)
  rows = 10;
  // Skeleton placeholder rows
  skeletonRows: { __skeleton: true; id: number }[] = Array.from({ length: 10 }, (_, i) => ({
    __skeleton: true,
    id: i,
  }));

  // Pagination properties
  currentPage = 0;
  rowsPerPage = 10;
  rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
  ];

  // Filter options
  typeOptions: any[] = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'Facebook', value: 'facebook' },
    { label: 'Google', value: 'google' },
  ];

  statusOptions: any[] = [
    { label: 'Active', value: 'active' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Stopped', value: 'stopped' },
    { label: 'Completed', value: 'completed' },
    { label: 'Draft', value: 'draft' },
    { label: 'Paused', value: 'paused' },
    { label: 'Resumed', value: 'resumed' },
    { label: 'Archived', value: 'archived' },
  ];

  locationOptions: any[] = [];

  // Filter selections
  selectedTypes: string[] = [];
  selectedStatuses: string[] = [];
  selectedLocations: string[] = [];

  // Filter panel visibility states
  isTypeFilterOpen = false;
  isStatusFilterOpen = false;
  isLocationFilterOpen = false;

  // Custom sort properties
  currentSortField = '';
  currentSortOrder: 'asc' | 'desc' | '' = '';

  // Material table properties
  dataSource = new MatTableDataSource<Campaign>();
  displayedColumns: string[] = [
    'campaign_name',
    'campaign_status',
    'location',
    'user_segment',
    'promo_code',
    'start_date',
    'platforms',
    'performance',
    'goals',
    'actions',
  ];

  currentCampaign: any = null;
  showDuplicateModal = false;
  showArchiveModal = false;
  showPauseModal = false;
  showStopModal = false;
  showViewModal = false;

  constructor(
    private profileService: ProfileService,
    private campaignService: CampaignService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private messageService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadLocations();
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (campaign: Campaign, property: string) => {
      switch (property) {
        case 'campaign_name':
          return campaign.name?.toLowerCase() ?? '';
        case 'start_date':
          return campaign.schedule_start_date ?? campaign.start_date ?? '';
        default:
          return String((campaign as unknown as Record<string, unknown>)[property] ?? '');
      }
    };
    // Don't load campaigns here - parent component handles initial load and passes data via @Input()
    // Campaigns will be loaded when filters change (handled in ngOnChanges)
  }

  private initializeDragScrolling(): void {
    if (!this._tableScrollWrapper?.nativeElement) return;

    // Cleanup and re-initialize using TableUtils
    const config = TableUtils.reinitializeDragScrolling(this._tableScrollWrapper);
    if (config) {
      this.dragScrollConfig = config;
    }
  }

  ngAfterViewInit(): void {
    this.initializeDragScrolling();
  }

  /**
   * Syncs input changes into the component: updates pagination from filters, triggers campaign reloads when filters change, and syncs totalRecords.
   * @example
   * gOnChanges({ filters: { page: 2, limit: 25 }, totalRecords: { currentValue: 100 } })
   * void
   * @param {SimpleChanges} changes - Object containing the changed input properties.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Sync pagination state from filters when they change
    if (changes['filters'] && this.filters) {
      // Update currentPage and rowsPerPage from filters (API uses 1-based page, component uses 0-based)
      if (this.filters.page !== undefined) {
        this.currentPage = this.filters.page - 1;
      }
      if (this.filters.limit !== undefined) {
        this.rowsPerPage = this.filters.limit;
      }

      // Only load campaigns when filters actually change (not on initial assignment)
      if (!changes['filters'].firstChange && changes['filters'].previousValue !== undefined) {
        this.loadCampaigns();
      }
    }

    // Sync totalRecords when it's passed from parent
    if (changes['totalRecords'] && changes['totalRecords'].currentValue !== undefined) {
      this.totalRecords = changes['totalRecords'].currentValue;
    }

    // Update dataSource when campaigns change
    if (changes['campaigns'] && this.campaigns) {
      this.dataSource.data = this.campaigns;
    }
  }

  /**
   * Loads campaigns from the server and updates the component state (sets loading, applies pagination filters, assigns campaigns and totalRecords, and clears loading on completion or error).
   * @example
   * oadCampaigns()
   * void
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Updates component state; does not return a value.
   **/
  loadCampaigns(): void {
    this.loading = true;

    const filters: CampaignFilters = {
      ...this.filters,
      page: this.currentPage + 1, // API uses 1-based pagination
      limit: this.rowsPerPage,
    };

    this.campaignService.getCampaigns(filters).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.campaigns = response.data.campaigns;
          this.totalRecords = parseInt(response.data.pagination.total, 10);
        }
        this.loading = false;
      },
      error: error => {
        console.error('Error loading campaigns:', error);
        this.loading = false;
        // You might want to show an error message to the user
      },
    });
  }

  loadLocations(): void {
    this.profileService.locations$.subscribe(locations => {
      this.locationOptions =
        locations?.map((loc: any) => ({ label: loc.branch, value: loc.id })) || [];
    });
    this.profileService.loadLocations();
  }

  setCurrentCampaign(campaign: any): void {
    this.currentCampaign = campaign;
  }

  onEditCampaign(): void {
    if (this.currentCampaign) {
      // Navigate directly to editor with campaignId (no 'new' parameter means editing)
      this.router.navigate(['/campaign-editor'], {
        queryParams: { campaignId: this.currentCampaign.id },
      });
    }
  }

  onDuplicateCampaign(): void {
    if (this.currentCampaign) {
      this.showDuplicateModal = true;
    }
  }

  /**
   * Duplicates the provided campaign by creating a new campaign from its payload, displays success/error messages, closes the duplicate modal and reloads the campaign list.
   * @example
   * nConfirmDuplicate({ id: 123, name: 'Spring Sale' })
   * undefined
   * @param {{any}} {{campaign}} - The campaign object to duplicate.
   * @returns {{void}} Void (no return value).
   **/
  onConfirmDuplicate(campaign: any): void {
    const duplicateCampaign = CampaignService.buildDuplicatePayload(campaign);

    this.campaignService.createCampaign(duplicateCampaign).subscribe({
      next: response => {
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'New campaign created!',
            detail: `"${campaign.name}" has been added successfully.`,
          });
          // Close duplicate modal
          this.showDuplicateModal = false;
          // Reload campaigns to show the new duplicated campaign
          this.loadCampaigns();
        } else {
          console.error('Failed to duplicate campaign:', response.message);
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to duplicate campaign!',
            detail: response.message,
          });
        }
      },
      error: error => {
        console.error('Error duplicating campaign:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to duplicate campaign!',
          detail: error.error?.message || 'An error occurred while duplicating the campaign.',
        });
      },
    });
  }

  onCloseDuplicateModal(): void {
    this.showDuplicateModal = false;
  }

  onDeleteCampaign(): void {
    if (this.currentCampaign) {
      this.delete.emit(this.currentCampaign);
    }
  }

  // Action button methods
  onPauseCampaign(campaign: any): void {
    this.setCurrentCampaign(campaign);
    this.showPauseModal = true;
  }

  /**
   * Toggle a campaign's paused state by sending an update request, updating local state, showing a message, and closing the pause modal.
   * @example
   * nConfirmPause({ id: 123, campaign_status: 'active', name: 'Spring Sale' })
   * undefined
   * @param {{any}} {{campaign}} - Campaign object containing at least id, campaign_status, and name.
   * @returns {{void}} Does not return a value; performs side effects (HTTP update, local state change, and user notification).
   **/
  onConfirmPause(campaign: any): void {
    const newStatus = campaign.campaign_status === 'paused' ? 'scheduled' : 'paused';

    const updatePayload = {
      campaign_status: newStatus,
      name: campaign.name,
    };

    this.campaignService.updateCampaign(campaign.id, updatePayload).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status in the local array
          const campaignIndex = this.campaigns.findIndex(c => c.id === campaign.id);
          if (campaignIndex !== -1) {
            this.campaigns[campaignIndex].campaign_status = newStatus;
          }
          this.messageService.add({
            severity: 'success',
            summary: `Campaign ${newStatus === 'paused' ? 'paused' : 'resumed'}!`,
            detail: `"${campaign.name}" has been ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully.`,
          });
        } else {
          console.error('Failed to update campaign status:', response.message);
        }
      },
      error: error => {
        console.error('Error updating campaign status:', error);
      },
    });

    this.showPauseModal = false;
  }

  onClosePauseModal(): void {
    this.showPauseModal = false;
  }

  onStopCampaign(campaign: any): void {
    this.setCurrentCampaign(campaign);
    this.showStopModal = true;
  }

  /**
   * Stop a campaign by sending an update to the server and updating the local list and UI.
   * @example
   * nConfirmStop({ id: 123, name: 'Spring Sale', campaign_status: 'active' })
   * undefined
   * @param {{any}} {{campaign}} - Campaign object containing at least `id` and `name`.
   * @returns {{void}} Void.
   **/
  onConfirmStop(campaign: any): void {
    const updatePayload = {
      campaign_status: 'stopped',
      name: campaign.name,
    };

    this.campaignService.updateCampaign(campaign.id, updatePayload).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status in the local array
          const campaignIndex = this.campaigns.findIndex(c => c.id === campaign.id);
          if (campaignIndex !== -1) {
            this.campaigns[campaignIndex].campaign_status = 'stopped';
          }
        } else {
          console.error('Failed to stop campaign:', response.message);
        }
      },
      error: error => {
        console.error('Error stopping campaign:', error);
      },
    });

    this.showStopModal = false;
  }

  onCloseStopModal(): void {
    this.showStopModal = false;
  }

  onMarkComplete(campaign: any): void {
    // Navigate to dashboard with campaign tab open
    this.router.navigate(['/dashboard'], { queryParams: { tab: 'Campaign Management' } });
  }

  // Action menu methods
  onViewCampaign(): void {
    if (this.currentCampaign) {
      this.showViewModal = true;
    }
  }

  onViewCampaignDetails(campaign: any): void {
    this.setCurrentCampaign(campaign);
    this.showViewModal = true;
  }

  onCloseViewModal(): void {
    this.showViewModal = false;
  }

  onCampaignDuplicated(): void {
    // Refresh the campaign table after duplication
    this.loadCampaigns();
  }

  onCampaignArchived(): void {
    // Refresh the campaign table after archiving
    this.loadCampaigns();
  }

  onCampaignPaused(): void {
    // Refresh the campaign table after pause/resume
    this.loadCampaigns();
  }

  onCampaignStopped(): void {
    // Refresh the campaign table after stopping
    this.loadCampaigns();
  }

  onCampaignMarkedComplete(): void {
    // Refresh the campaign table after marking complete
    this.loadCampaigns();
  }

  onArchiveCampaign(): void {
    if (this.currentCampaign) {
      this.showArchiveModal = true;
    }
  }

  /**
   * Archives a campaign by calling the campaign service, updates the local campaign status, shows a success message, and closes the archive modal.
   * @example
   * nConfirmArchive({ id: 123, name: 'Spring Sale' })
   * undefined
   * @param {{any}} {{campaign}} - Campaign object containing at minimum an id and name.
   * @returns {{void}} Void; no value is returned.
   **/
  onConfirmArchive(campaign: any): void {
    if (!campaign) return;

    this.campaignService.archiveCampaign(campaign.id, campaign.name).subscribe({
      next: response => {
        if (response.status) {
          // Update the campaign status in the local array
          const campaignIndex = this.campaigns.findIndex(c => c.id === campaign.id);
          if (campaignIndex !== -1) {
            this.campaigns[campaignIndex].campaign_status = 'archived';
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Campaign archived!',
            detail: `"${campaign.name}" has been moved to archive.`,
          });
        } else {
          console.error('Failed to archive campaign:', response.message);
        }
      },
      error: error => {
        console.error('Error archiving campaign:', error);
      },
    });

    this.showArchiveModal = false;
  }

  onCloseArchiveModal(): void {
    this.showArchiveModal = false;
  }

  onAddCampaign(): void {
    this.router.navigate(['/campaign-editor'], {
      queryParams: { new: 'true' },
    });
  }

  showActionMenu(event: Event, campaign: Campaign): void {
    event.preventDefault();
    event.stopPropagation();
    this.setCurrentCampaign(campaign);
  }

  getPlatformKey(platform: string | { platform?: string }): string {
    return typeof platform === 'string' ? platform : platform.platform || '';
  }

  // Filter methods
  toggleTypeFilter(event: Event) {
    this.isTypeFilterOpen = !this.isTypeFilterOpen;
  }

  applyTypeFilter() {
    this.isTypeFilterOpen = false;

    // Apply filter logic here
  }

  clearTypeFilter() {
    this.selectedTypes = [];
    this.isTypeFilterOpen = false;

    // Clear filter logic here
  }

  toggleStatusFilter(event: Event) {
    this.isStatusFilterOpen = !this.isStatusFilterOpen;
  }

  applyStatusFilter() {
    this.isStatusFilterOpen = false;

    // Apply filter logic here
  }

  clearStatusFilter() {
    this.selectedStatuses = [];
    this.isStatusFilterOpen = false;

    // Clear filter logic here
  }

  toggleLocationFilter(event: Event) {
    this.isLocationFilterOpen = !this.isLocationFilterOpen;
  }

  applyLocationFilter() {
    this.isLocationFilterOpen = false;

    // Apply filter logic here
  }

  clearLocationFilter() {
    this.selectedLocations = [];
    this.isLocationFilterOpen = false;

    // Clear filter logic here
  }

  // Getters for filter states
  get hasTypeFilterValues(): boolean {
    return this.selectedTypes.length > 0;
  }

  get hasStatusFilterValues(): boolean {
    return this.selectedStatuses.length > 0;
  }

  get hasLocationFilterValues(): boolean {
    return this.selectedLocations.length > 0;
  }

  // Check if there are active filters or search
  get hasActiveFiltersOrSearch(): boolean {
    return (
      this.hasTypeFilterValues ||
      this.hasStatusFilterValues ||
      this.hasLocationFilterValues ||
      (this.filters &&
        Object.keys(this.filters).some(
          key => key !== 'page' && key !== 'limit' && this.filters[key as keyof CampaignFilters]
        ))
    );
  }

  // Get empty state configuration based on whether filters are active
  /**
   * Determine the appropriate empty-state configuration for the campaign table based on original campaign count, current campaigns array, and loading state.
   * @example
   * emptyStateConfig()
   * {
   *   title: 'No campaigns to show',
   *   description: 'Start adding campaigns and track all campaign details here. ',
   *   showAddButton: true
   * }
   * @returns {{Object}} Returns an object with `title`, `description`, and `showAddButton` fields describing the UI empty state.
   **/
  get emptyStateConfig() {
    const config = (() => {
      // If no campaigns exist at all (original count is 0), show add button
      if (this.originalCampaignCount === 0 && this.hasLoadedInitialCampaigns) {
        return {
          title: 'No campaigns to show',
          description: 'Start adding campaigns and track all campaign details here. ',
          showAddButton: true,
        };
      }
      // If campaigns exist originally but current view is empty (due to filters/search), don't show add button
      else if (this.originalCampaignCount > 0 && this.campaigns.length === 0) {
        return {
          title: 'No results found!',
          description: 'Please try again or modify your search or filters.',
          showAddButton: false,
        };
      }
      // Default case - loading state or edge case
      else if (!this.hasLoadedInitialCampaigns && this.campaigns.length === 0) {
        return {
          title: 'No campaigns to show',
          description: 'Create your first campaign to start engaging with your customers.',
          showAddButton: false,
        };
      }
      return {
        title: 'No campaigns found',
        description: '',
        showAddButton: false,
      };
    })();

    return config;
  }

  // Sort methods
  getSortIcon(field: string): string {
    return CampaignSortUtils.getSortIcon(field, this.currentSortField, this.currentSortOrder);
  }

  customSort(event: any): void {
    CampaignSortUtils.customSort(event, this, this.cdr);
  }

  // Get campaign type display name
  getCampaignTypeDisplay(type: string): string {
    const typeMap: Record<string, string> = {
      email: 'Email',
      sms: 'Text Message',
      facebook: 'Facebook',
      google: 'Google',
      physical_mailer: 'Physical Mailer',
    };
    return typeMap[type] || type;
  }

  getStatusDisplay(status: string): string {
    return CampaignService.getStatusDisplay(status);
  }

  // Get location display name
  getLocationDisplay(locationId: string): string {
    const location = this.locationOptions.find(loc => loc.value === locationId);
    return location ? location.label : locationId;
  }

  // Get location tooltip text
  getLocationTooltipText(campaign: Campaign): string {
    if (campaign.locations && campaign.locations.length > 0) {
      return campaign.locations.map(location => `• ${location.branch}`).join('\n');
    }
    return 'No locations';
  }

  // Get segments tooltip text
  getSegmentsTooltipText(campaign: Campaign): string {
    if (campaign.segments && campaign.segments.length > 0) {
      return campaign.segments.map(segment => `• ${segment.name}`).join('\n');
    }
    return 'No segments';
  }

  // Get additional goals tooltip text
  getMoreGoalsTooltipText(campaign: Campaign): string {
    if (campaign.goals && campaign.goals.length > 0) {
      return campaign.goals.map(goal => `• ${this.formatGoalDisplay(goal)}`).join('\n');
    }
    return 'No goals set';
  }

  /**
   * Safe goals-column label. Avoids "[object Object]" when target_value is nested
   * or otherwise non-primitive (bad payload / partial parse).
   */
  formatGoalDisplay(goal: { goal_name?: string | null; target_value?: unknown }): string {
    const name =
      typeof goal?.goal_name === 'string' && goal.goal_name.trim() ? goal.goal_name.trim() : 'Goal';
    return `${name}: 0 / (${this.formatGoalTarget(goal?.target_value)})`;
  }

  private formatGoalTarget(target: unknown): number {
    if (typeof target === 'number' && Number.isFinite(target)) {
      return target;
    }
    if (typeof target === 'string') {
      const parsed = parseInt(target, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (target && typeof target === 'object') {
      const nested =
        (target as { value?: unknown; target_value?: unknown; amount?: unknown }).value ??
        (target as { target_value?: unknown }).target_value ??
        (target as { amount?: unknown }).amount;
      return this.formatGoalTarget(nested);
    }
    return 0;
  }

  // Mobile/tablet detection
  // Pagination methods
  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.rowsPerPage = event.rows;
    this.loadCampaigns();
  }

  onRowsPerPageChange(event: any): void {
    this.rowsPerPage = event.value;
    this.currentPage = 0; // Reset to first page when changing rows per page
    this.loadCampaigns();
  }

  getEndItem(): number {
    return Math.min((this.currentPage + 1) * this.rowsPerPage, this.totalRecords);
  }

  trackBySkeletonId(_: number, row: { id: number }): number {
    return row.id;
  }
  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
  trackByIndex(index: number): number {
    return index;
  }
  trackByGoalName(_: number, goal: { goal_name: string }): string {
    return goal.goal_name;
  }

  // Method to apply filters from parent component
  applyFilters(filters: CampaignFilters): void {
    this.filters = { ...filters };
    this.currentPage = 0; // Reset to first page when applying filters
    this.loadCampaigns();
  }

  // Method to clear filters
  clearFilters(): void {
    this.filters = {};
    this.currentPage = 0;
    this.loadCampaigns();
  }

  // Format created date in "month date year" format (e.g., "Jan 15 2024")
  formatCreatedDate(dateString: string | null | undefined): string {
    return formatDateMonthDayYear(dateString);
  }

  // Format start date in "month date year" format (e.g., "Jan 15 2024")
  formatStartDate(dateString: string | null | undefined): string {
    if (!dateString) {
      return '—';
    }
    return formatDateMonthDayYear(dateString) || '—';
  }
}
